import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ScrollSection from '../components/how-it-works/ScrollSection';
import HeroAnimation from '../components/how-it-works/HeroAnimation';
import ProblemAnimation from '../components/how-it-works/ProblemAnimation';
import SolutionAnimation from '../components/how-it-works/SolutionAnimation';
import MoneyAnimation from '../components/how-it-works/MoneyAnimation';
import RealtimeAnimation from '../components/how-it-works/RealtimeAnimation';

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-900 font-semibold">
            <span className="text-xl">🏷️</span>
            Auction Platform
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/demo" className="text-sm text-gray-600 hover:text-gray-900">
              Try Demo
            </Link>
            <Link
              to="/login"
              className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              No More Auction Chaos
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Ever wondered how online auctions keep track of hundreds of bids without getting confused? 
              Here's our secret sauce. 🍳
            </p>
          </motion.div>
          <HeroAnimation />
        </div>
      </section>

      {/* Section 1: The Problem */}
      <ScrollSection className="py-16 px-4 bg-red-50/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-red-100 text-red-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
              The Problem
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ever Lost a Bid You Should've Won? 🤔
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              When two people bid at the exact same moment, traditional systems get confused. 
              Who was first? Which bid counts? It's a recipe for frustrated bidders.
            </p>
          </div>
          <ProblemAnimation />
        </div>
      </ScrollSection>

      {/* Section 2: The Solution */}
      <ScrollSection className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-green-100 text-green-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
              The Solution
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Every Bid Gets Its Turn ✨
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Each auction has its own dedicated "assistant" (we call it an Actor) that handles 
              bids one at a time, in the order they arrive. No cutting in line!
            </p>
          </div>
          <SolutionAnimation />
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-4 text-center">
              <div className="text-2xl mb-1">🎯</div>
              <div className="text-sm text-gray-500">First come</div>
              <div className="font-semibold text-gray-900">First served</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-4 text-center">
              <div className="text-2xl mb-1">⚡</div>
              <div className="text-sm text-gray-500">Response time</div>
              <div className="font-semibold text-gray-900">&lt;50ms</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-4 text-center">
              <div className="text-2xl mb-1">🔒</div>
              <div className="text-sm text-gray-500">Race conditions</div>
              <div className="font-semibold text-gray-900">Zero</div>
            </div>
          </div>
        </div>
      </ScrollSection>

      {/* Section 3: Your Money */}
      <ScrollSection className="py-16 px-4 bg-purple-50/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-purple-100 text-purple-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
              Your Money
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Funds Always Protected 💰
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              When you bid, we put a temporary hold on your funds. Win the auction? We capture the payment. 
              Get outbid? Your money's instantly available again. Magic!
            </p>
          </div>
          <MoneyAnimation />
        </div>
      </ScrollSection>

      {/* Section 4: Real-time */}
      <ScrollSection className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-blue-100 text-blue-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
              Real-time
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everyone Sees The Same Thing 📡
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The moment someone places a bid, everyone watching gets the update instantly. 
              No refreshing, no wondering if you're seeing old data.
            </p>
          </div>
          <RealtimeAnimation />
        </div>
      </ScrollSection>

      {/* CTA Section */}
      <ScrollSection className="py-20 px-4 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Try It? 🚀
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
              Our demo lets you see everything in action. Create auctions, place bids, 
              watch the real-time updates. No sign-up required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 font-semibold px-8 py-4 rounded-xl hover:bg-blue-50 transition-colors shadow-lg"
              >
                <span>Try the Demo</span>
                <span>→</span>
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-blue-500 text-white font-semibold px-8 py-4 rounded-xl hover:bg-blue-400 transition-colors"
              >
                <span>Create Account</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </ScrollSection>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-gray-400">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm mb-4">
            Built with the Actor Model pattern on Cloudflare's edge network.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/architecture" className="text-purple-400 hover:text-purple-300">
              🔧 Technical Deep Dive →
            </Link>
            <Link to="/" className="text-blue-400 hover:text-blue-300">
              ← Back to Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
